'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { ChevronDown, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '@/lib/utils';
import { sortLimitsAscending } from '@/lib/sort-limits';
import { useStepperContext } from '../ui/stepper';
import { useSessionFooter } from './NewSessionStepper';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';

export type CashGameSessionLine = {
  bankrollId: string;
  variant: string;
  stakes: string;
  tableType: string;
  handsCount: string;
  profit: string;
};

interface SessionData {
  format?: string;
  universe?: 'Online' | 'Live';
  bankrolls?: Map<string, { bankrollId: string; newBalance: string }>;
  cashGameVariantLines?: { bankrollId?: string; variant: string; stakes: string; tableType?: string | null; handsCount?: string | number | null; profit?: string | number | null }[] | null;
  duration?: string;
}

interface NewSessionStep2LimitsVariantsCashGameProps {
  onComplete: (data: {
    cashGameVariantLines: { bankrollId: string; variant: string; stakes: string; tableType: string | null; handsCount: number | null; profit: number | null }[];
    bankrolls: Map<string, { bankrollId: string; newBalance: string }>;
    duration?: string;
  }) => void;
  sessionData: SessionData;
}

const STAKES_OPTIONS = [
  '0.01/0.02', '0.02/0.05', '0.05/0.10', '0.10/0.20', '0.25/0.50', '0.50/1', '1/2', '2/4', '2/5', '3/6', '5/10', '10/20', '15/30',
];
const TABLE_TYPE_OPTIONS = ['Full Ring', '6-max', 'HU'];
const VARIANT_OPTIONS = [
  "No Limit Hold'em", "Limit Hold'em", 'OMAHA 4', 'OMAHA 5', 'OMAHA HI/LO', 'SHORT TRACK', '8-GAME', 'HORSE', 'SHORT DECK', 'RAZZ', 'DEALER CHOICE',
];

export const NewSessionStep2LimitsVariantsCashGame: React.FC<NewSessionStep2LimitsVariantsCashGameProps> = ({
  onComplete,
  sessionData,
}) => {
  const t = useTranslations('sessions.new.stepper.step2Sessions');
  const { nextStep, previousStep } = useStepperContext();
  const setFooter = useSessionFooter()?.setFooter;
  const universe = sessionData.universe || 'Online';
  const [stakesOptions, setStakesOptions] = useState<string[]>(STAKES_OPTIONS);
  const [variantOptions, setVariantOptions] = useState<string[]>(VARIANT_OPTIONS);
  const [tableTypeOptions, setTableTypeOptions] = useState<string[]>(TABLE_TYPE_OPTIONS);
  const stakesOptionsForUniverse = universe === 'Live'
    ? stakesOptions.slice(Math.max(stakesOptions.indexOf('0.50/1'), 0))
    : stakesOptions;

  const selectedBankrollIds = Array.from(sessionData.bankrolls?.keys() || []);
  const firstBankrollId = selectedBankrollIds[0] ?? '';

  const [lines, setLines] = useState<CashGameSessionLine[]>(() => {
    const raw = sessionData.cashGameVariantLines;
    if (raw?.length) {
      return raw.map(l => ({
        bankrollId: (l as { bankrollId?: string }).bankrollId ?? firstBankrollId,
        variant: l.variant ?? '',
        stakes: l.stakes ?? '',
        tableType: l.tableType ?? '',
        handsCount: l.handsCount != null ? String(l.handsCount) : '',
        profit: l.profit != null ? String(l.profit) : '',
      }));
    }
    // Une ligne par défaut par bankroll pour ne pas obliger à cliquer sur « Ajouter une session »
    if (selectedBankrollIds.length) {
      return selectedBankrollIds.map(bankrollId => ({
        bankrollId,
        variant: '',
        stakes: '',
        tableType: '',
        handsCount: '',
        profit: '',
      }));
    }
    return [];
  });
  const [bankrolls, setBankrolls] = useState<{ id: string; siteName: string; currency: string; currentBalance: string }[]>([]);
  /** Index de la ligne dont le dropdown Limites est ouvert, ou null */
  const [openLimitsLineIndex, setOpenLimitsLineIndex] = useState<number | null>(null);
  /** Position fixe du dropdown (pour affichage en portail au-dessus du scroll) */
  const [limitsDropdownRect, setLimitsDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const limitsTriggerRef = useRef<HTMLDivElement>(null);
  const limitsPanelRef = useRef<HTMLDivElement>(null);

  const closeLimitsDropdown = useCallback(() => {
    setOpenLimitsLineIndex(null);
    setLimitsDropdownRect(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        openLimitsLineIndex !== null &&
        !limitsTriggerRef.current?.contains(target) &&
        !limitsPanelRef.current?.contains(target)
      ) {
        closeLimitsDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openLimitsLineIndex, closeLimitsDropdown]);

  /** Parse stakes string (simple ou "a, b") en tableau pour les cases à cocher */
  const getStakesArray = (stakes: string): string[] => {
    if (!stakes.trim()) return [];
    return stakes.split(',').map(s => s.trim()).filter(Boolean);
  };

  const setLineStakesArray = (lineIndex: number, selected: string[]) => {
    const sorted = sortLimitsAscending(selected);
    setLines(prev => prev.map((l, i) => (i === lineIndex ? { ...l, stakes: sorted.join(', ') } : l)));
  };

  useEffect(() => {
    const raw = sessionData.cashGameVariantLines;
    const ids = Array.from(sessionData.bankrolls?.keys() || []);
    const firstId = ids[0] ?? '';
    if (raw?.length) {
      setLines(raw.map(l => {
        const arr = (l.stakes ?? '').split(',').map(s => s.trim()).filter(Boolean);
        const stakesNormalized = arr.length > 0 ? sortLimitsAscending(arr).join(', ') : '';
        return {
          bankrollId: (l as { bankrollId?: string }).bankrollId ?? firstId,
          variant: l.variant ?? '',
          stakes: stakesNormalized,
          tableType: l.tableType ?? '',
          handsCount: l.handsCount != null ? String(l.handsCount) : '',
          profit: l.profit != null ? String(l.profit) : '',
        };
      }));
    }
  }, [sessionData.cashGameVariantLines, sessionData.bankrolls]);

  useEffect(() => {
    const fetchBankrolls = async () => {
      try {
        const res = await fetch('/api/bankrolls');
        const data = await res.json();
        if (data.bankrolls) {
          const ids = Array.from(sessionData.bankrolls?.keys() || []);
          const selected = data.bankrolls.filter((b: { id: string }) => ids.includes(b.id));
          setBankrolls(selected.map((b: { id: string; siteName: string; currency: string; currentBalance: string }) => ({
            id: b.id,
            siteName: b.siteName,
            currency: b.currency,
            currentBalance: b.currentBalance,
          })));
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchBankrolls();
  }, [sessionData.bankrolls]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const response = await fetch('/api/reference-data');
        const result = await response.json();
        if (!response.ok || !result?.data) return;
        if (Array.isArray(result.data.cashGameLimits) && result.data.cashGameLimits.length > 0) {
          setStakesOptions(result.data.cashGameLimits);
        }
        if (Array.isArray(result.data.cashGameVariants) && result.data.cashGameVariants.length > 0) {
          setVariantOptions(result.data.cashGameVariants);
        }
        if (Array.isArray(result.data.cashGameTableTypes) && result.data.cashGameTableTypes.length > 0) {
          setTableTypeOptions(result.data.cashGameTableTypes);
        }
      } catch {
        // fallback silencieux
      }
    };
    loadReferenceData();
  }, []);

  const handleLineChange = (lineIndex: number, field: keyof CashGameSessionLine, value: string) => {
    const normalized =
      field === 'handsCount' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value;
    setLines(prev => prev.map((l, i) => (i === lineIndex ? { ...l, [field]: normalized } : l)));
  };

  const addSession = (bankrollId: string) => {
    setLines(prev => [...prev, { bankrollId, variant: '', stakes: '', tableType: '', handsCount: '', profit: '' }]);
  };

  const removeLine = (lineIndex: number) => {
    setLines(prev => prev.filter((_, i) => i !== lineIndex));
  };

  const handleContinue = useCallback(() => {
    const parseProfitInput = (v: string): number => {
      if (!v || !v.trim()) return 0;
      const n = parseFloat(v.trim().replace(/\s/g, '').replace(',', '.'));
      return Number.isNaN(n) ? 0 : n;
    };
    const hasValidLine = lines.some(l => l.variant.trim() !== '' && getStakesArray(l.stakes).length > 0 && l.profit.trim() !== '');
    if (!hasValidLine) return;
    const updates = new Map(sessionData.bankrolls || new Map());
    bankrolls.forEach(br => {
      const bankrollLines = lines.filter(l => l.bankrollId === br.id);
      const totalProfit = bankrollLines.reduce((s, l) => s + parseProfitInput(l.profit), 0);
      const currentBalance = parseFloat(br.currentBalance);
      const entry = updates.get(br.id);
      if (entry) updates.set(br.id, { ...entry, newBalance: String(currentBalance + totalProfit) });
    });
    onComplete({
      cashGameVariantLines: lines.map(l => ({
        bankrollId: l.bankrollId,
        variant: l.variant,
        stakes: l.stakes,
        tableType: l.tableType || null,
        handsCount: l.handsCount ? parseInt(l.handsCount, 10) : null,
        profit: l.profit.trim() ? parseProfitInput(l.profit) : null,
      })),
      bankrolls: updates,
      duration: sessionData.duration,
    });
    nextStep();
  }, [lines, sessionData.bankrolls, sessionData.duration, bankrolls, onComplete, nextStep]);

  const isValid = lines.length > 0 && lines.every(l => l.variant.trim() !== '' && getStakesArray(l.stakes).length > 0 && l.profit.trim() !== '');

  useEffect(() => {
    if (!setFooter) return;
    setFooter({
      secondary: {
        label: t('previous'),
        onClick: previousStep,
      },
      primary: {
        label: t('continue'),
        disabled: !isValid,
        onClick: handleContinue,
      },
    });
    return () => setFooter(null);
  }, [setFooter, t, isValid, handleContinue, previousStep]);

  const openLimitsLine = openLimitsLineIndex !== null ? lines[openLimitsLineIndex] : null;

  return (
    <div className="space-y-6">
      {typeof document !== 'undefined' &&
        openLimitsLineIndex !== null &&
        limitsDropdownRect !== null &&
        openLimitsLine &&
        createPortal(
          <div
            ref={limitsPanelRef}
            className="fixed z-[101] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-[min(70vh,28rem)] overflow-y-auto pt-1 pb-3"
            style={{
              top: limitsDropdownRect.top,
              left: limitsDropdownRect.left,
              width: limitsDropdownRect.width,
            }}
          >
            {stakesOptionsForUniverse.map((option) => {
              const selected = getStakesArray(openLimitsLine.stakes).includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    const current = getStakesArray(openLimitsLine.stakes);
                    const next = selected ? current.filter((s) => s !== option) : [...current, option];
                    setLineStakesArray(openLimitsLineIndex!, next);
                  }}
                  className={cn(
                    'w-full px-3 py-2.5 text-left rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2',
                    selected && 'bg-gold/10 dark:bg-gold/20'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    className="w-4 h-4 text-gold border-gray-300 dark:border-gray-600 rounded focus:ring-gray-900 dark:focus:ring-gray-100"
                  />
                  <span>{option}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('title')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
      </div>

      {/* Une section par bankroll */}
      {selectedBankrollIds.map(bankrollId => {
        const bankroll = bankrolls.find(b => b.id === bankrollId);
        const bankrollLines = lines.filter(l => l.bankrollId === bankrollId);
        if (!bankroll) return null;
        return (
          <div key={bankrollId} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <PokerSiteLogo siteName={bankroll.siteName} size={32} className="rounded-lg overflow-hidden" />
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{bankroll.siteName}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{bankrollLines.length} {t('sessionsCount')}</p>
                </div>
              </div>
              <Button
                type="button"
                onClick={() => addSession(bankrollId)}
                className="bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
              >
                + {t('addSession')}
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {bankrollLines.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">{t('noSessions')}</p>
              ) : (
                bankrollLines.map((line, localIndex) => {
                  const globalIndex = lines.findIndex(l => l === line);
                  const profitNum = parseFloat(line.profit) || 0;
                  return (
                    <div key={globalIndex} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 items-end p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 min-w-0 overflow-x-auto">
                      <div className="min-w-0">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('variant')} *</label>
                        <select
                          value={line.variant}
                          onChange={(e) => handleLineChange(globalIndex, 'variant', e.target.value)}
                          className="w-full h-10 px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 min-w-0"
                        >
                          <option value="">{t('variantPlaceholder')}</option>
                          {variantOptions.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                        </select>
                      </div>
                      <div className="min-w-0 relative" ref={globalIndex === openLimitsLineIndex ? limitsTriggerRef : undefined}>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('limits')} *</label>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (openLimitsLineIndex === globalIndex) {
                              closeLimitsDropdown();
                            } else {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setLimitsDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                              setOpenLimitsLineIndex(globalIndex);
                            }
                          }}
                          className={cn(
                            'w-full h-10 px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 min-w-0 flex items-center justify-between text-left',
                            getStakesArray(line.stakes).length > 0 && 'border-gold dark:border-gold'
                          )}
                        >
                          <span className={getStakesArray(line.stakes).length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                            {getStakesArray(line.stakes).length > 0 ? sortLimitsAscending(getStakesArray(line.stakes)).join(', ') : t('limitsPlaceholder')}
                          </span>
                          <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                        </button>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('type')}</label>
                        <select
                          value={line.tableType}
                          onChange={(e) => handleLineChange(globalIndex, 'tableType', e.target.value)}
                          className="w-full h-10 px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 min-w-0"
                        >
                          <option value="">{t('typePlaceholder')}</option>
                          {tableTypeOptions.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('result')} ({bankroll.currency}) *</label>
                        <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 dark:focus-within:ring-gray-100">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.profit}
                            onChange={(e) => handleLineChange(globalIndex, 'profit', e.target.value)}
                            placeholder="0"
                            className="border-0 focus-visible:ring-0 rounded-none bg-transparent min-w-0"
                          />
                          <span className="pr-3 flex items-center min-w-[1.25rem] justify-end shrink-0">
                            {profitNum > 0 && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                            {profitNum < 0 && <TrendingDown className="w-4 h-4 text-red-500" />}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 whitespace-nowrap">{t('handsCount')}</label>
                        <Input
                          type="number"
                          min="0"
                          value={line.handsCount}
                          onChange={(e) => handleLineChange(globalIndex, 'handsCount', e.target.value)}
                          placeholder="0"
                          className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 min-w-0"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(globalIndex)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0 self-end mb-0.5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
